import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { MixpanelService } from 'src/app/services/mixpanel/mixpanel.service';

// The code most recently exchanged in this tab
const LAST_CODE_KEY = 'workos_last_code';

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['../../main/main.component.scss', './auth-callback.component.scss'],
})
export class AuthCallbackComponent implements OnInit {
  public error: string | null;
  public loading = true;
  // WorkOS logout URL included by the backend in the callback failure response.
  // Captured so "Try again" can clear WorkOS's session before returning to login.
  private logoutUrl: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private mixpanelService: MixpanelService
  ) { }

  ngOnInit() {
    const code = this.route.snapshot.queryParamMap.get('code');

    if (!code) {
      this.loading = false;
      this.error = 'Login link is missing required information. Please try logging in again.';
      this.mixpanelService.track('Action: WorkOS Callback Missing Code');
      return;
    }

    // A code works once. Coming back to this URL (the Back button, a reload) while still logged
    // in would send the spent code again, be refused, and offer a "Try again" that ends a
    // perfectly good session. A different code is always exchanged, even when logged in: that
    // is how support staff start impersonating a user.
    if (code === sessionStorage.getItem(LAST_CODE_KEY) && this.authService.isLoggedIn()) {
      this.router.navigate(['dashboard'], { replaceUrl: true });
      return;
    }

    this.authService.handleCallback(code).subscribe(
      () => {
        sessionStorage.setItem(LAST_CODE_KEY, code);
        this.mixpanelService.track('Action: WorkOS Login Success');
        // replaceUrl takes the callback, and its code, out of the browser history
        const redirectUrl = this.authService.consumePostLoginRedirect();
        if (redirectUrl) {
          this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
        } else {
          this.router.navigate(['dashboard'], { replaceUrl: true });
        }
      },
      (err) => {
        console.error('WorkOS callback error', err);
        this.loading = false;
        this.logoutUrl = err?.error?.logout_url ?? null;
        if (err.status === 401) {
          this.error = 'Your login session has expired or is invalid. Please try logging in again.';
        } else if (err.status === 403) {
          this.error = err?.error?.detail ?? 'Your account does not have access to this application. Please contact your administrator.';
        } else {
          this.error = 'We were unable to log you in. Please try again.';
        }
        this.mixpanelService.track('Action: WorkOS Login Failure');
      }
    );
  }

  // Send the user through WorkOS's logout URL so its session cookie is cleared;
  // otherwise WorkOS would silently re-sign them in with the same identity on
  // the next login attempt, looping on the same access denial. The backend can
  // only build this URL while it still holds the session_id from the failed
  // callback exchange, so it ships the URL inline with the error response.
  // Falls back to a local /login nav if no logout URL was provided (e.g. the
  // failure happened before WorkOS auth — missing code, network error).
  retry() {
    this.authService.clearLocalSession();
    if (this.logoutUrl) {
      this.authService.redirectToExternalUrl(this.logoutUrl);
    } else {
      this.router.navigate(['login']);
    }
  }
}
